"use client";

import { useEffect, useRef, useState } from "react";
import { fetchCorpusDocument } from "@/app/lib/api";
import type { CorpusDocumentResponse, DeepResearchSource } from "@/app/lib/types";
import MarkdownRenderer from "./MarkdownRenderer";

type ViewerState =
  | { kind: "loading" }
  | { kind: "loaded"; document: CorpusDocumentResponse }
  | { kind: "error"; message: string };

function failureMessage(error: unknown): string {
  const status = typeof error === "object" && error !== null && "status" in error
    ? (error as { status?: unknown }).status
    : undefined;
  if (status === 403) return "You do not have a licensed corpus seat, so this document cannot be opened.";
  if (status === 404) return "This document was not found in either the live corpus or the cited archive.";
  if (status === 503) return "The licensed corpus is temporarily unavailable. Please try again later.";
  return "The licensed corpus document could not be opened. Please try again.";
}

function archiveDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export type CorpusDocumentContentDestination = "text" | "studio-document";

/** Decides whether a fetched corpus document can be handed off to the Studio. */
export function corpusDocumentContentDestination(
  document: CorpusDocumentResponse,
  studioUrl?: string,
): CorpusDocumentContentDestination {
  return studioUrl ? "studio-document" : "text";
}

interface CorpusDocumentViewerProps {
  source: DeepResearchSource;
  onClose: () => void;
}

export default function CorpusDocumentViewer({ source, onClose }: CorpusDocumentViewerProps) {
  const [state, setState] = useState<ViewerState>({ kind: "loading" });
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let active = true;
    closeButtonRef.current?.focus();
    fetchCorpusDocument(source.url)
      .then((document) => { if (active) setState({ kind: "loaded", document }); })
      .catch((error: unknown) => { if (active) setState({ kind: "error", message: failureMessage(error) }); });
    const handleKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKeyDown);
    return () => { active = false; window.removeEventListener("keydown", handleKeyDown); };
  }, [source.url, onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Licensed corpus document"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1001,
        background: "rgba(0, 0, 0, 0.96)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <article
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "75vw",
          height: "90vh",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-xl)",
          padding: "10px 24px 20px 24px",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header style={{ display: "flex", flexShrink: 0, justifyContent: "space-between", alignItems: "center", gap: "16px", marginBottom: "16px", paddingBottom: "16px", borderBottom: "1px solid var(--border)" }}>
          <div><div style={{ color: "#cc6144", fontSize: "13px", fontWeight: 600 }}>Licensed corpus material</div><h1 style={{ margin: "4px 0 0", fontSize: "24px" }}>{state.kind === "loaded" ? state.document.title : source.title}</h1></div>
          <button ref={closeButtonRef} className="btn-ghost" onClick={onClose}>Close</button>
        </header>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", padding: "0 clamp(0px, 2vw, 48px) 44px" }}>
          {state.kind === "loading" && <p style={{ color: "var(--text-secondary)" }}>Opening licensed corpus document…</p>}
          {state.kind === "error" && <p role="alert" style={{ color: "var(--error)" }}>{state.message}</p>}
          {state.kind === "loaded" && <>
            {state.document.copy === "archived" && (
              <div role="note" style={{ marginBottom: "20px", padding: "12px 14px", border: "1px solid #cc6144", borderRadius: "var(--radius-md)", background: "color-mix(in srgb, #cc6144 8%, transparent)" }}>
                <strong>This is the archived copy cited by the report.</strong> It was archived on {state.document.archived_at ? archiveDate(state.document.archived_at) : "an unknown date"}, rather than the current live corpus copy.
              </div>
            )}
            <div style={{ marginBottom: "24px", color: "var(--text-secondary)", fontSize: "13px" }}>{state.document.byte_length.toLocaleString()} bytes</div>
            {(source.publisher_url || source.studio_url) && <aside aria-label="Document provenance" style={{ marginBottom: "24px", padding: "12px 14px", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", color: "var(--text-secondary)", fontSize: "13px" }}>
              <strong style={{ color: "var(--text-primary)" }}>Provenance</strong>
              {source.publisher_url && <div style={{ marginTop: "6px" }}><a href={source.publisher_url} target="_blank" rel="noopener noreferrer">Publisher link</a></div>}
              {source.studio_url && <div style={{ marginTop: "6px" }}><a href={source.studio_url} target="_blank" rel="noopener noreferrer">Private document-server link</a></div>}
            </aside>}
            {corpusDocumentContentDestination(state.document, source.studio_url) === "studio-document" ? (
              <section aria-label="Studio document" style={{ padding: "16px", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", background: "var(--surface)" }}>
                <p style={{ margin: "0 0 12px" }}>Open the original document in the document server.</p>
                <a className="btn-primary" href={source.studio_url} target="_blank" rel="noopener noreferrer">Open document in document server</a>
              </section>
            ) : <MarkdownRenderer content={state.document.text} isStreamComplete />}
          </>}
        </div>
      </article>
    </div>
  );
}
